// src/useAgentSSE.ts
import { useUserContext } from '@/contexts/UserContext';
import { getUserEnvironment } from '@/utils/getUserEnvironment';
import type { AgentRunAttachment, InterruptResponse } from '@superfunnel/types';
import { httpsCallable } from 'firebase/functions';
import { MutableRefObject, useCallback, useRef } from 'react';
import { functions } from '../Firebase';
import { sessionManager } from '../services/SessionService';
import {
  SSEGenericEvent,
  SSETokenEvent,
  StreamEvent,
} from '../types/agentChat';

// Environment-aware URLs
const isDevelopment = window.location.hostname === 'localhost';
const devFunctionsUrl =
  import.meta.env.VITE_FUNCTIONS_URL ||
  'http://localhost:5001/ai-marketing-agents-406e0/europe-west1';

const getAgentSSEUrl = (agentName: string) => {
  const baseUrl = isDevelopment
    ? devFunctionsUrl
    : 'https://europe-west1-ai-marketing-agents-406e0.cloudfunctions.net';

  // Map agent names to their respective endpoints
  const endpointMap: Record<string, string> = {
    landingPageAgent: 'landingpageagentsse',
    assistantAgent: 'assistantagentsse',
    htmlStepperFormAgent: 'htmlstepperformagentsse',
    // Add more agents here as needed
  };

  const endpoint = endpointMap[agentName] || 'landingpageagentsse';
  return `${baseUrl}/${endpoint}`;
};

export interface UseAgentSSEOptions {
  onStreamEvent?: (event: StreamEvent) => void;
  onStreamComplete?: (finalText: string, events: SSEGenericEvent[]) => void;
  onError?: (error: Error) => void;
}

type StartAgentPayload = {
  accountId: string;
  agentName: string;
  prompt: string;
  threadId: string;
  runContext?: any;
  attachments?: any[];
};

/**
 * Attaches all SSE event listeners to an EventSource instance.
 * Shared between start() and resume() to avoid duplication.
 */
function attachSSEEventListeners(
  es: EventSource,
  agentName: string,
  currentStreamRef: MutableRefObject<{
    text: string;
    events: SSEGenericEvent[];
    runId: string;
  } | null>,
  esRef: MutableRefObject<EventSource | null>,
  options: UseAgentSSEOptions,
) {
  es.addEventListener('token', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as SSETokenEvent;
    if (currentStreamRef.current) {
      currentStreamRef.current.text += data.value ?? '';
      options.onStreamEvent?.({
        type: 'token',
        data: data.value,
      });
    }
  });

  es.addEventListener('message_start', (e) => {
    const data = JSON.parse((e as MessageEvent).data);
    options.onStreamEvent?.({
      type: 'message_start',
      data,
    });
  });

  es.addEventListener('message_end', (e) => {
    const data = JSON.parse((e as MessageEvent).data);
    options.onStreamEvent?.({
      type: 'message_end',
      data,
    });
  });

  es.addEventListener('working', (e) => {
    const data = JSON.parse((e as MessageEvent).data);
    options.onStreamEvent?.({
      type: 'working',
      data,
    });
  });

  es.addEventListener('tool_call_start', (e) => {
    const data = JSON.parse((e as MessageEvent).data);
    options.onStreamEvent?.({
      type: 'tool_call_start',
      data,
    });
  });

  es.addEventListener('tool_call_end', (e) => {
    const data = JSON.parse((e as MessageEvent).data);
    options.onStreamEvent?.({
      type: 'tool_call_end',
      data,
    });
  });

  es.addEventListener('interrupt', (e) => {
    const data = JSON.parse((e as MessageEvent).data);
    options.onStreamEvent?.({
      type: 'interrupt',
      data,
    });
    // Close the connection to prevent automatic reconnection.
    // The user will need to explicitly call resume() to continue.
    es.close();
    esRef.current = null;
  });

  es.addEventListener('resume', (e) => {
    const data = JSON.parse((e as MessageEvent).data);
    options.onStreamEvent?.({
      type: 'resume',
      data,
    });
  });

  es.addEventListener('event', (e) => {
    const data = JSON.parse((e as MessageEvent).data) as SSEGenericEvent;
    if (currentStreamRef.current) {
      currentStreamRef.current.events.push(data);
      options.onStreamEvent?.({
        type: 'event',
        data,
      });
    }
  });

  es.addEventListener('done', () => {
    if (currentStreamRef.current) {
      options.onStreamComplete?.(
        currentStreamRef.current.text,
        currentStreamRef.current.events,
      );
    }
    es.close();
    esRef.current = null;
    currentStreamRef.current = null;
  });

  es.onerror = (e) => {
    // IMPORTANT: Close immediately to prevent EventSource auto-reconnection
    es.close();
    esRef.current = null;

    // onerror receives an Event, not MessageEvent - there's no .data property
    // Try to extract error info if available, but don't throw if parsing fails
    let errorMessage = `SSE connection error for agent ${agentName}`;
    try {
      const messageEvent = e as MessageEvent;
      if (messageEvent.data) {
        const data = JSON.parse(messageEvent.data);
        if (data.message) {
          errorMessage = data.message;
        }
      }
    } catch {
      // Parsing failed - use default error message
    }

    const error = new Error(errorMessage);
    options.onError?.(error);
    currentStreamRef.current = null;
  };
}

export function useAgentSSE(
  agentName: string,
  options: UseAgentSSEOptions = {},
) {
  const { selectedAccount } = useUserContext();
  const esRef = useRef<EventSource | null>(null);
  const currentStreamRef = useRef<{
    text: string;
    events: SSEGenericEvent[];
    runId: string;
  } | null>(null);

  const stop = useCallback(async () => {
    const runId = currentStreamRef.current?.runId;

    // Close the EventSource connection
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    // Send cancellation request to backend if we have a runId and account
    if (runId && selectedAccount) {
      try {
        const cancelFunction = httpsCallable<
          { accountId: string; runId: string },
          { success: boolean }
        >(functions, 'cancelAgentRun');
        await cancelFunction({ accountId: selectedAccount.id, runId });
        console.log('Agent run cancelled', { runId });
      } catch (error) {
        console.error('Failed to cancel agent run', { runId, error });
      }
    }

    currentStreamRef.current = null;
  }, [selectedAccount]);

  const start = useCallback(
    async (
      prompt: string,
      threadId: string,
      runContext?: Record<string, any>,
      attachments?: AgentRunAttachment[],
    ): Promise<string> => {
      try {
        if (!selectedAccount) {
          throw new Error('No account selected');
        }

        // Ensure we have a valid session before making the request
        await sessionManager.ensureValidSession();

        // Get the appropriate function for this agent
        const startFunction = httpsCallable<
          StartAgentPayload,
          { runId: string }
        >(functions, 'startAgent');

        const requestPayload: StartAgentPayload = {
          accountId: selectedAccount.id,
          agentName,
          prompt,
          threadId,
        };

        // Always inject user environment context
        const userEnvironment = getUserEnvironment();

        if (runContext && Object.keys(runContext).length > 0) {
          requestPayload.runContext = {
            ...runContext,
            userEnvironment,
          };
        } else {
          requestPayload.runContext = { userEnvironment };
        }

        if (localStorage.getItem('devMode') === 'true') {
          requestPayload.runContext = {
            ...requestPayload.runContext,
            devMode: true,
          };
        }

        if (attachments && attachments.length > 0) {
          requestPayload.attachments = attachments;
        }

        const resp = await startFunction(requestPayload);
        const { runId } = resp.data;

        // Close any existing stream
        stop();

        // Initialize stream state
        currentStreamRef.current = {
          text: '',
          events: [],
          runId,
        };

        // Open SSE to the agent-specific endpoint
        const url = new URL(getAgentSSEUrl(agentName));
        url.searchParams.set('runId', runId);

        const es = new EventSource(url.toString(), { withCredentials: true });
        esRef.current = es;

        attachSSEEventListeners(
          es,
          agentName,
          currentStreamRef,
          esRef,
          options,
        );

        return runId;
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error('Unknown error starting agent stream');
        options.onError?.(err);
        throw err;
      }
    },
    [agentName, options, selectedAccount, stop],
  );

  const getCurrentStream = useCallback(() => {
    return currentStreamRef.current;
  }, []);

  /**
   * Resume an interrupted agent run with the user's response.
   * Reconnects to the same SSE endpoint with the resume response.
   */
  const resume = useCallback(
    async (runId: string, response: InterruptResponse): Promise<boolean> => {
      if (!selectedAccount) {
        console.error('No account selected');
        return false;
      }

      try {
        // Ensure we have a valid session
        await sessionManager.ensureValidSession();

        // Close any existing stream
        if (esRef.current) {
          esRef.current.close();
          esRef.current = null;
        }

        // Reinitialize stream state
        currentStreamRef.current = {
          text: '',
          events: [],
          runId,
        };

        // Reconnect to SSE with the resume response as a query parameter
        const url = new URL(getAgentSSEUrl(agentName));
        url.searchParams.set('runId', runId);
        url.searchParams.set(
          'resumeResponse',
          encodeURIComponent(JSON.stringify(response)),
        );

        const es = new EventSource(url.toString(), { withCredentials: true });
        esRef.current = es;

        attachSSEEventListeners(
          es,
          agentName,
          currentStreamRef,
          esRef,
          options,
        );

        return true;
      } catch (error) {
        console.error('Error resuming agent run:', error);
        options.onError?.(
          error instanceof Error ? error : new Error('Failed to resume agent'),
        );
        return false;
      }
    },
    [agentName, options, selectedAccount],
  );

  return { start, stop, resume, getCurrentStream };
}
